import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Modifier } from '../../types/character';
import { useTheme } from '../../theme';

export interface ModifierBreakdown {
  target: string;
  baseValue: number;
  finalValue: number;
  modifiers: Array<{
    id: string;
    source: string;
    operation: string;
    value: number;
    resolvedValue: number;
    description?: string;
  }>;
}

interface StatTooltipProps {
  children: React.ReactNode;
  breakdown?: ModifierBreakdown;
  statName: string;
  className?: string;
}

const StatTooltip: React.FC<StatTooltipProps> = ({ 
  children, 
  breakdown, 
  statName, 
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  const formatSource = (source: string): { label: string; color: string } => {
    if (source.startsWith('equipment:')) {
      return { label: 'Equipment', color: 'var(--semantic-colors-content-equipment)' };
    }
    if (source.startsWith('spell:')) {
      return { label: 'Spell', color: 'var(--semantic-colors-content-spell)' };
    }
    if (source.startsWith('class:')) {
      return { label: 'Class Feature', color: 'var(--semantic-colors-content-class)' };
    }
    if (source.startsWith('manual:')) {
      return { label: 'Manual', color: 'var(--semantic-colors-content-manual)' };
    }
    return { label: 'Other', color: 'var(--semantic-colors-text-secondary)' };
  };

  const formatOperation = (operation: string, value: number): string => {
    switch (operation) {
      case 'add': return value >= 0 ? '+' : '';
      case 'multiply': return '×';
      case 'set': return '=';
      case 'replace': return '→';
      default: return operation;
    }
  };

  const formatValue = (operation: string, value: number): string => {
    switch (operation) {
      case 'add': return value >= 0 ? value.toString() : value.toString(); // Negative values already have the minus sign
      case 'multiply': return `${value}`;
      case 'set': return value.toString();
      case 'replace': return value.toString();
      default: return value.toString();
    }
  };

  if (!breakdown || breakdown.modifiers.length === 0) {
    return <div className={className}>{children}</div>;
  }

  const hasModifiers = breakdown.finalValue !== breakdown.baseValue;

  return (
    <>
      <div 
        ref={containerRef}
        className={`stat-tooltip-trigger ${className} ${hasModifiers ? 'stat-tooltip-trigger--modified' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="stat-tooltip"
            style={{
              position: 'fixed',
              left: position.x,
              top: position.y,
              zIndex: 1000,
              pointerEvents: 'none'
            }}
            initial={{ opacity: 0, y: 5, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            <div className="stat-tooltip__content">
              <div className="stat-tooltip__header">
                <h4 className="stat-tooltip__title">{statName} Calculation</h4>
              </div>
              
              <div className="stat-tooltip__breakdown">
                <div className="stat-tooltip__base">
                  <span className="stat-tooltip__label">Base Value:</span>
                  <span className="stat-tooltip__value">{breakdown.baseValue}</span>
                </div>

                {breakdown.modifiers.map((modifier, index) => {
                  const sourceInfo = formatSource(modifier.source);
                  return (
                    <motion.div
                      key={modifier.id}
                      className="stat-tooltip__modifier"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="stat-tooltip__modifier-source">
                        <span 
                          className="stat-tooltip__source-dot"
                          style={{ backgroundColor: sourceInfo.color }}
                        />
                        <span className="stat-tooltip__source-label">
                          {sourceInfo.label}
                        </span>
                      </div>
                      <div className="stat-tooltip__modifier-operation">
                        <span className="stat-tooltip__operation">
                          {formatOperation(modifier.operation, modifier.resolvedValue)}
                        </span>
                        <span className="stat-tooltip__modifier-value">
                          {formatValue(modifier.operation, modifier.resolvedValue)}
                        </span>
                      </div>
                      {modifier.description && (
                        <div className="stat-tooltip__modifier-description">
                          {modifier.description}
                        </div>
                      )}
                    </motion.div>
                  );
                })}

                <div className="stat-tooltip__final">
                  <span className="stat-tooltip__label">Final Value:</span>
                  <span className="stat-tooltip__value stat-tooltip__value--final">
                    {breakdown.finalValue}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StatTooltip;